import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Waves } from "lucide-react";
import { api, imgUrl } from "@/lib/api";

const TITLES = {
  "aviso-legal": "Aviso Legal",
  "privacidad": "Política de Privacidad y Protección de Datos",
  "cookies": "Política de Cookies",
  "terminos": "Términos y Condiciones",
};

const Section = ({ title, children }) => (
  <div className="mt-8">
    <h2 className="font-display text-xl font-bold text-[#1C1917] md:text-2xl">{title}</h2>
    <div className="mt-2 space-y-3 text-[15px] leading-relaxed text-[#44403C]">{children}</div>
  </div>
);

const Identity = ({ s }) => (
  <ul className="space-y-1">
    <li><b>Razón social:</b> {s.legal_name || "—"}</li>
    <li><b>CIF/NIF:</b> {s.legal_cif || "—"}</li>
    <li><b>Domicilio:</b> {s.legal_address || s.location_address || "—"}</li>
    <li><b>Correo electrónico:</b> {s.legal_email || "—"}</li>
    {s.legal_phone ? <li><b>Teléfono:</b> {s.legal_phone}</li> : null}
    <li><b>Nombre comercial:</b> {s.restaurant_name || "Chiringuito Salitre"}</li>
    <li><b>Sitio web:</b> {typeof window !== "undefined" ? window.location.host : "chiringuitosalitre.es"}</li>
  </ul>
);

const ALCOHOL_DISCLAIMER = 'Este comerciante se compromete a no permitir ninguna transacción que sea ilegal, o se considere por las marcas de tarjetas de crédito o el banco adquiriente, que pueda o tenga el potencial de dañar la buena voluntad de los mismos o influir de manera negativa en ellos. Las siguientes actividades están prohibidas en virtud de los programas de las marcas de tarjetas: la venta u oferta de un producto o servicio que no sea de plena conformidad con todas las leyes aplicables al Comprador, Banco Emisor, Comerciante, Titular de la tarjeta, o tarjetas. Además, las siguientes actividades también están prohibidas explícitamente: "Venta de bebidas alcohólicas a menores de 18 años".';

function AvisoLegal({ s }) {
  return (
    <>
      <Section title="1. Identificación del titular">
        <p>En cumplimiento de la Ley 34/2002 de Servicios de la Sociedad de la Información y de Comercio Electrónico (LSSI-CE), se informa de que el titular de este sitio web es:</p>
        <Identity s={s} />
      </Section>
      <Section title="2. Objeto y actividad">
        <p>Este sitio web tiene por objeto la presentación de las cartas del establecimiento de restauración {s.restaurant_name || "Chiringuito Salitre"} y la reserva y venta de entradas para eventos organizados por el establecimiento.</p>
      </Section>
      <Section title="3. Condiciones de uso">
        <p>El acceso y uso de este sitio web atribuye la condición de usuario e implica la aceptación de las presentes condiciones. El usuario se compromete a hacer un uso adecuado de los contenidos y a no emplearlos para actividades ilícitas.</p>
      </Section>
      <Section title="4. Propiedad intelectual e industrial">
        <p>Todos los contenidos del sitio (textos, fotografías, logotipos, diseño) son titularidad del establecimiento o de terceros que han autorizado su uso, y están protegidos por la normativa de propiedad intelectual e industrial. Queda prohibida su reproducción sin autorización.</p>
      </Section>
      <Section title="5. Responsabilidad">
        <p>El titular no se hace responsable de los daños derivados del mal uso del sitio ni de las interrupciones ajenas a su control. La información de las cartas puede variar; los alérgenos indicados son orientativos y el cliente debe consultar al personal en caso de alergia o intolerancia.</p>
      </Section>
      <Section title="6. Legislación aplicable">
        <p>Las presentes condiciones se rigen por la legislación española. Para cualquier controversia serán competentes los juzgados y tribunales del domicilio del titular, salvo que la normativa de consumidores disponga otro fuero.</p>
      </Section>
    </>
  );
}

function Privacidad({ s }) {
  return (
    <>
      <Section title="1. Responsable del tratamiento">
        <Identity s={s} />
      </Section>
      <Section title="2. Datos que tratamos y finalidad">
        <p>Cuando realizas una reserva de entradas recabamos: nombre, correo electrónico, teléfono, número de personas y notas que aportes. Estos datos se utilizan para gestionar tu reserva, enviarte la confirmación y, en su caso, tramitar el pago.</p>
      </Section>
      <Section title="3. Base jurídica">
        <p>El tratamiento se basa en la ejecución del contrato (tu reserva) y en tu consentimiento. La conservación se limita al tiempo necesario para prestar el servicio y cumplir las obligaciones legales (fiscales y contables).</p>
      </Section>
      <Section title="4. Destinatarios y encargados">
        <p>Para prestar el servicio utilizamos proveedores que actúan como encargados del tratamiento: la pasarela de pago <b>Redsys</b> y la entidad bancaria (procesamiento del cobro con tarjeta), el proveedor de mensajería <b>Twilio</b> (aviso interno por WhatsApp/SMS) y el proveedor de envío de correo (email de confirmación). No cedemos tus datos a terceros salvo obligación legal.</p>
      </Section>
      <Section title="5. Tus derechos">
        <p>Puedes ejercer los derechos de acceso, rectificación, supresión, oposición, limitación y portabilidad escribiendo a {s.legal_email || "el correo de contacto del establecimiento"}. También puedes reclamar ante la Agencia Española de Protección de Datos (www.aepd.es).</p>
      </Section>
    </>
  );
}

function Cookies({ s }) {
  return (
    <>
      <Section title="1. ¿Qué son las cookies?">
        <p>Las cookies son pequeños archivos que se almacenan en tu dispositivo al navegar. Este sitio utiliza el almacenamiento local del navegador únicamente para recordar tu preferencia de idioma y tu aceptación de esta política.</p>
      </Section>
      <Section title="2. Cookies que utilizamos">
        <ul className="list-disc pl-5">
          <li><b>Técnicas / necesarias:</b> imprescindibles para el funcionamiento del sitio (preferencia de idioma, sesión del administrador, aceptación de cookies). No requieren consentimiento.</li>
          <li><b>De terceros:</b> al mostrar el mapa de ubicación se carga Google Maps, que puede instalar sus propias cookies. Consulta la política de Google.</li>
        </ul>
        <p>No utilizamos cookies publicitarias ni de perfilado.</p>
      </Section>
      <Section title="3. Gestión">
        <p>Puedes bloquear o eliminar las cookies desde la configuración de tu navegador. Al hacerlo, algunas funciones podrían no operar correctamente.</p>
      </Section>
    </>
  );
}

function Terminos({ s }) {
  return (
    <>
      <Section title="1. Objeto">
        <p>Estas condiciones regulan la reserva y compra de entradas para eventos de {s.restaurant_name || "Chiringuito Salitre"} a través de este sitio web, cuyo titular se identifica a continuación:</p>
        <Identity s={s} />
      </Section>
      <Section title="2. Proceso de compra y forma de pago">
        <p>El usuario selecciona el evento, indica el número de entradas y sus datos, y realiza el pago. <b>Los cobros se efectúan mediante tarjeta bancaria (Visa/Mastercard) a través de la pasarela de pago segura Redsys</b> de la entidad bancaria. Los precios se muestran en euros e incluyen los impuestos aplicables. La reserva se confirma una vez recibido el pago; recibirás un correo de confirmación.</p>
      </Section>
      <Section title="3. Disponibilidad">
        <p>El número de entradas por evento es limitado. Cuando se agotan, la venta se cierra automáticamente.</p>
      </Section>
      <Section title="4. Devoluciones y cancelaciones">
        <p>Al tratarse de entradas para eventos y servicios de ocio con fecha determinada, de acuerdo con el art. 103.l) del RD Legislativo 1/2007 (Ley General para la Defensa de Consumidores y Usuarios), <b>no aplica el derecho de desistimiento</b>. No obstante, si el evento se cancela o se modifica sustancialmente por causas imputables al establecimiento, se reembolsará el importe abonado. Para cualquier solicitud, escribe a {s.legal_email || "el correo de contacto del establecimiento"}.</p>
      </Section>
      <Section title="5. Venta de bebidas alcohólicas y actividades prohibidas">
        <p>Está <b>prohibida la venta y el consumo de bebidas alcohólicas a menores de 18 años</b>. El establecimiento podrá solicitar documento identificativo.</p>
        <p className="rounded-xl bg-secondary p-4 text-sm text-[#57534E]">{ALCOHOL_DISCLAIMER}</p>
      </Section>
      <Section title="6. Legislación y fuero">
        <p>Estas condiciones se rigen por la legislación española y por la normativa de protección de consumidores y usuarios.</p>
      </Section>
    </>
  );
}

const RENDER = { "aviso-legal": AvisoLegal, "privacidad": Privacidad, "cookies": Cookies, "terminos": Terminos };

export default function Legal() {
  const { type } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState({});
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  const Content = RENDER[type];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="border-b border-[#E7E5E4] bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
          <button data-testid="legal-back" onClick={() => navigate("/")} className="flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-semibold text-[#57534E] hover:bg-secondary"><ChevronLeft className="h-4 w-4" /> Inicio</button>
          <div className="flex items-center gap-2 text-[#075985]">
            {s.logo ? <img src={imgUrl(s.logo)} alt="" className="h-8 w-auto object-contain" /> : <Waves className="h-5 w-5" />}
            <span className="font-display text-lg font-bold">{s.restaurant_name || "Chiringuito Salitre"}</span>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-5 py-10 pb-24">
        <h1 className="font-display text-3xl font-bold text-[#1C1917] md:text-4xl">{TITLES[type] || "Información legal"}</h1>
        {Content ? <Content s={s} /> : <p className="mt-6 text-[#57534E]">Página no encontrada.</p>}
      </main>
    </div>
  );
}
